/**
 * Auth gate for nested routes. Renders <Outlet> only once a session is confirmed.
 *
 * While the initial /me check is in flight it shows a spinner (prevents a
 * flash of the login page). Redirects: no user -> /login; user with a forced
 * password reset -> /force-change-password; user whose role is not in the
 * optional `roles` list -> /unauthorized.
 */
import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function ProtectedRoute({ roles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.must_change_password) {
    return <Navigate to="/force-change-password" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
}

const styles = {
  loading: {
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--color-mint)',
  },
  spinner: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: '3px solid var(--color-mint-dark)',
    borderTopColor: 'var(--color-forest)',
  },
};
